import { api } from '@/lib/axios';
import { mitStand } from '@/lib/speichern';
import type { ModuleKey, ModuleStatus } from '@symphenia/shared-types';
import type { EigenerSpeicher, SpeicherUebersicht } from '@/lib/speicher';

/**
 * AP-29-E — Betreiber-Konsole.
 *
 * Die einzigen Endpunkte im ganzen Frontend, die über Mandanten hinweg gehen.
 * Sie liegen hinter dem `PlatformAdminGuard`; wer nicht zum Betreiber gehört,
 * bekommt 403 — auch als SUPER_ADMIN seines eigenen Mandanten.
 */

export type BookingStatus = 'TRIAL' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED';
export type SeatMode = 'ALL_USERS' | 'ASSIGNED';

export interface OperatorTenantRow {
  id: string;
  name: string;
  subdomain: string;
  plan: string;
  isActive: boolean;
  isOperator: boolean;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  /** ISO-Zeitpunkt des Löschantrags; null, wenn keiner vorliegt. */
  deletionRequestedAt: string | null;
  /** Nur aktive Benutzer — sie sind es, die Plätze belegen. */
  activeUsers: number;
  moduleCount: number;
  modules: string[];
  hasTrial: boolean;
}

export interface OperatorBooking {
  status: BookingStatus;
  seatMode: SeatMode;
  seats: number | null;
  /** Belegt von aktiven Benutzern. */
  seatsUsed: number;
  /** Zuteilungen deaktivierter Benutzer — sie zählen nicht, bleiben aber stehen. */
  seatsDormant: number;
  bookedAt: string;
  trialEndsAt: string | null;
  validUntil: string | null;
  cancelledAt: string | null;
  endsAt: string | null;
  note: string | null;
  /** Herkunft und Schnappschuss des Bündels (AP-29-N-G). */
  packageKey?: string | null;
  packageName?: string | null;
  packageContents?: string[];
  /** Was dieser Kunde für diesen Baustein zahlt — eingefroren beim Buchen. */
  price?: BookingPrice | null;
}

export interface OperatorModuleRow {
  key: ModuleKey;
  status: ModuleStatus;
  bookable: boolean;
  /**
   * AP-29-N: Die Gruppe, zu der dieser Eintrag gehört — `null` bei einem
   * eigenständigen Modul. Die Oberfläche gruppiert danach (Schritt H); sie
   * zerlegt **nicht** den Schlüssel am Punkt, der ist nur für Menschen da.
   */
  parent: ModuleKey | null;
  booking: OperatorBooking | null;
}

/** AP-29-N-I: Der eingefrorene Preis einer Buchung — was dieser Kunde zahlt. */
export interface BookingPrice {
  amount: number;
  currency: string;
  basis: 'PER_SEAT' | 'FLAT' | null;
  period: 'MONTH' | 'YEAR' | null;
}

export interface OperatorTenantModules {
  tenant: { id: string; name: string; subdomain: string; activeUsers: number };
  modules: OperatorModuleRow[];
}

export interface ListTenantsParams {
  search?: string;
  approvalStatus?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedTenants {
  data: OperatorTenantRow[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface BookModulePayload {
  moduleKey: string;
  seatMode?: SeatMode;
  seats?: number;
  trialEndsAt?: string;
  validUntil?: string;
  assignUserIds?: string[];
  note?: string;
}

export interface UpdateBookingPayload {
  status?: 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
  /** AP-29-N2: der laufende Preis dieser Buchung; `null` nimmt ihn weg. */
  priceAmount?: number | null;
  priceCurrency?: string;
  priceBasis?: 'PER_SEAT' | 'FLAT';
  pricePeriod?: 'MONTH' | 'YEAR';
  seatMode?: SeatMode;
  seats?: number;
  trialEndsAt?: string;
  validUntil?: string;
  endsAt?: string;
  assignUserIds?: string[];
  note?: string;
}


/**
 * AP-29-N — Was der Betreiber zu einem Posten festgelegt hat.
 *
 * **Beide Angaben sind optional.** `salesName` leer heißt: Es gilt die
 * Bezeichnung aus den i18n-Dateien. `amount` leer heißt: noch nicht bepreist —
 * ein zulässiger Zustand, kein Fehler.
 *
 * Der **Bezug** ist der Teil, der sonst fehlt: `8` allein ist nicht
 * interpretierbar. `PER_SEAT` heißt je Platz, `FLAT` pauschal.
 */
export interface CatalogRow {
  itemKey: string;
  salesName: string | null;
  amount: number | null;
  currency: string;
  basis: 'PER_SEAT' | 'FLAT';
  period: 'MONTH' | 'YEAR';
}

/**
 * Was ein Paket kostet. Der **festgesetzte** Preis gilt; `sumOfParts` steht nur
 * daneben, damit der Betreiber sieht, wie viel Bündelrabatt er gerade gibt.
 */
export interface PackagePricing {
  price: CatalogRow | null;
  sumOfParts: number | null;
  /** Bausteine des Pakets ohne gepflegten Preis. */
  unpriced: string[];
}

/**
 * AP-29-E — Ein Verkaufsbündel.
 *
 * Pakete stehen in der Datenbank, nicht im Code: Was heute „Handwerk komplett"
 * heißt, kann morgen anders geschnitten sein, ohne dass jemand ein Deployment
 * braucht. Beim Buchen wird ein Paket **flach aufgelöst** — gebucht wird immer
 * je Modul, das Paket hinterlässt nur seine Herkunft.
 */
export interface ModulePackage {
  id: string;
  key: string;
  name: string;
  description: string | null;
  moduleKeys: string[];
  packageKeys: string[];
  isActive: boolean;
  sortOrder: number;
  /** Flach aufgelöst — das, was beim Buchen entsteht. */
  resolvedModuleKeys: string[];
  /** Enthaltenes, das sich nicht (mehr) buchen lässt. Leer ist der Normalfall. */
  problems: string[];
  /** AP-29-N-I: Festsetzung und Summe der Teile nebeneinander. */
  pricing: PackagePricing | null;
}

export interface SaveModulePackagePayload {
  name: string;
  description?: string;
  moduleKeys: string[];
  packageKeys: string[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface BookPackagePayload {
  seatMode?: SeatMode;
  seats?: number;
  trialEndsAt?: string;
  validUntil?: string;
  assignUserIds?: string[];
  note?: string;
}

/**
 * AP-29-N-C — Umbuchen auf eine **Zielmenge**.
 *
 * Nicht „tu dies", sondern „so soll es danach aussehen" — die Differenz bildet
 * der Server. Mit `dryRun` kommt dieselbe Antwort zurück, ohne dass etwas
 * geschrieben wird; genau das ist die Vorschau in der Konsole.
 */
export interface SetModulesPayload {
  moduleKeys: string[];
  packageKey?: string;
  dryRun?: boolean;
  seatMode?: SeatMode;
  seats?: number;
  trialEndsAt?: string;
  validUntil?: string;
  note?: string;
}

export interface SetModulesResult {
  /** neu anzulegen */
  add: string[];
  /** abgelaufen, wird wiederbelebt (die Buchung bleibt dieselbe) */
  revive: string[];
  /** läuft bereits, bleibt unangetastet */
  keep: string[];
  /** läuft, steht aber nicht im Ziel */
  cancel: string[];
  /** nicht verkaufbar — überspringen heißt nicht verschweigen */
  blocked: { moduleKey: string; reason: string }[];
}

/**
 * AP-29-N2 — Listenpreis und vereinbarter Preis nebeneinander.
 *
 * Beide zusammen, nie einer statt des anderen: Ohne die Liste beantwortet die
 * Maske die Frage nicht, die beim Nachverhandeln gestellt wird — wie viel
 * Rabatt bekommt dieser Kunde eigentlich?
 */
export interface TenantPriceEntry {
  amount: number;
  currency: string;
  basis: CatalogRow['basis'];
  period: CatalogRow['period'];
  note: string | null;
  /** Ab welchem Tag diese Zusage gilt (ISO). */
  validFrom: string;
}

export interface TenantPriceRow {
  itemKey: string;
  salesName: string | null;
  list: CatalogRow;
  /** Was **heute** gilt — die jüngste Zusage, deren Datum erreicht ist. */
  agreed: TenantPriceEntry | null;
  /** Alle Zusagen, aufsteigend: abgelöste, geltende und geplante. */
  entries: TenantPriceEntry[];
}

/** AP-29-H/N2 — Wer beim Mandanten einen Platz hat. Der Betreiber sieht es, verteilt aber nicht. */
export interface SeatOverview {
  module: {
    key: string;
    seatMode: SeatMode;
    seats: number | null;
    seatsUsed: number;
    seatsDormant: number;
  };
  users: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    roles: { key: string; name: string }[];
    assigned: boolean;
  }[];
}

export interface BookPackageResult {
  packageKey: string;
  booked: string[];
  /** Übersprungen heißt nicht verschwiegen — die Konsole zeigt es an. */
  skipped: { moduleKey: string; reason: string }[];
}

/** AP-10 Nachtrag 3 — Mail-Kontingent eines Mandanten im laufenden Monat. */
export interface OperatorMailQuota {
  month: string;
  used: number;
  base: number;
  extra: number;
  limit: number;
}

/** Speicher eines Mandanten (AP-11-E4) — dieselbe Übersicht, die der Mandant unter „Speicher" sieht. */
export type OperatorStorage = SpeicherUebersicht;

/** Eigener Speicher eines Mandanten (AP-11-E5) — ohne Zugangsdaten; die sieht auch der Betreiber nie. */
export type OperatorOwnStorage = EigenerSpeicher;

/** Symphenia-KI-Kontingent eines Mandanten im laufenden Monat (30.9.2026). */
export interface OperatorAiQuota {
  month: string;
  used: number;
  /** Tokens je Monat über den Schlüssel von Symphenia; 0 = kein Kontingent. */
  limit: number;
  /** Eigener Schlüssel hinterlegt — für den gilt keine Grenze. */
  ownKey: boolean;
}

/** AP-43 — KI-Vorgaben: Modell je Aufgabe für alle Mandanten ohne eigene Wahl. */
export type AiTaskDefaultsProviderKey = 'ANTHROPIC' | 'OPENAI';

/** Die umgeleiteten Wege für zentral abgerechnete Claude-Aufrufe (`AI_ANTHROPIC_VIA`). */
export type AiCentralRoute = 'bedrock' | 'google';

export interface AiTaskDefaultsProvider {
  provider: AiTaskDefaultsProviderKey;
  /** Vorgabe im Code — greift, wenn die Konsole leer ist. */
  defaultModel: string;
  /** `route`: der Weg, der das Modell trägt; false = umgeleitet, aber nirgends zugeordnet, nicht wählbar; null = der zentrale Weg ist nicht umgeleitet. */
  models: { id: string; route: AiCentralRoute | false | null }[];
}

export interface AiTaskDefaultsTask {
  task: string;
  label: string;
  module: string | null;
}

/** Eine gespeicherte Vorgabe; `model: null` heißt „keine Zeile". */
export interface AiTaskDefaultRow {
  provider: AiTaskDefaultsProviderKey;
  task: string;
  model: string | null;
  valid?: boolean;
  reason?: 'TASK_UNKNOWN' | 'MODEL_UNKNOWN' | 'NOT_MAPPED';
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface AiTaskDefaults {
  /**
   * `routes` in der Reihenfolge, in der sie gefragt werden; `ready: false` = gewählt, aber nicht benutzbar —
   * `reason` sagt warum: `CREDENTIALS` (Zugangsdaten fehlen) oder `NOT_EU` (Region außerhalb der EU).
   * `rejected`: Einträge der Zuordnung, deren Kennung nicht auf die EU zeigt — in der Produktion gelten sie nicht.
   *
   * Nur EU (10.10.2026): `euOnly` — die Produktion erzwingt die EU; `directBlocked` — `AI_ANTHROPIC_VIA` ist
   * leer oder `direct` und trägt dort keinen Aufruf; `openaiBlocked` — `AI_OPENAI_KEY` ist gesetzt und wird nicht benutzt.
   */
  via: {
    active: boolean;
    euOnly?: boolean;
    directBlocked?: boolean;
    openaiBlocked?: boolean;
    routes: { key: AiCentralRoute; region: string; ready: boolean; reason?: 'CREDENTIALS' | 'NOT_EU' | null; rejected?: { model: string; kennung: string }[] }[];
  };
  providers: AiTaskDefaultsProvider[];
  tasks: AiTaskDefaultsTask[];
  rows: AiTaskDefaultRow[];
}

/** AP-38-E — Stand der Zwei-Faktor-Einführung. */
export interface ZweiFaktorEinfuehrung {
  frist: string | null;
  abgeschaltet: boolean;
  konten: { gesamt: number; eingerichtet: number; ausstehend: number };
  versand: Record<'ADMINS' | 'ALLE', { am: string; anzahl: number } | null>;
}

export const operatorApi = {
  // ── Zwei-Faktor-Einführung (AP-38-E) ──────────────────────────────────────

  zweiFaktor: async (): Promise<ZweiFaktorEinfuehrung> => {
    const res = await api.get<{ data: ZweiFaktorEinfuehrung }>('/admin/two-factor');
    return res.data.data;
  },

  zweiFaktorAnkuendigen: async (zielgruppe: 'ADMINS' | 'ALLE'): Promise<{ anzahl: number }> => {
    const res = await api.post<{ data: { anzahl: number } }>('/admin/two-factor/announce', { zielgruppe });
    return res.data.data;
  },

  zweiFaktorZuruecksetzen: async (email: string): Promise<{ email: string }> => {
    const res = await api.post<{ data: { email: string } }>('/admin/two-factor/reset', { email });
    return res.data.data;
  },

  // ── Mail-Kontingent (AP-10 Nachtrag 3) ────────────────────────────────────

  getMailQuota: async (tenantId: string): Promise<OperatorMailQuota> => {
    const res = await api.get<OperatorMailQuota>(`/admin/tenants/${tenantId}/mail-quota`);
    return res.data;
  },

  setMailQuota: async (tenantId: string, extraPerMonth: number): Promise<OperatorMailQuota> => {
    const res = await api.put<OperatorMailQuota>(`/admin/tenants/${tenantId}/mail-quota`, { extraPerMonth });
    return res.data;
  },

  // ── Speicher-Kontingent (AP-11-E4) ────────────────────────────────────────

  getStorage: async (tenantId: string): Promise<OperatorStorage> => {
    const res = await api.get<OperatorStorage>(`/admin/tenants/${tenantId}/storage`);
    return res.data;
  },

  /** `null` nimmt den eigenen Wert zurück — dann gilt der Startwert des Plans. */
  setStorageQuota: async (tenantId: string, limitGb: number | null): Promise<OperatorStorage> => {
    const res = await api.put<OperatorStorage>(`/admin/tenants/${tenantId}/storage-quota`, { limitGb });
    return res.data;
  },

  // ── Eigener Speicher (AP-11-E5) ───────────────────────────────────────────

  getOwnStorage: async (tenantId: string): Promise<OperatorOwnStorage> => {
    const res = await api.get<OperatorOwnStorage>(`/admin/tenants/${tenantId}/own-storage`);
    return res.data;
  },

  /** Gibt den Rückweg auf den Symphenia-Speicher frei oder nimmt die Freigabe zurück. */
  setOwnStorageReturn: async (tenantId: string, approved: boolean): Promise<OperatorOwnStorage> => {
    const res = await api.put<OperatorOwnStorage>(`/admin/tenants/${tenantId}/own-storage/return-approval`, { approved });
    return res.data;
  },

  // ── Symphenia-KI-Kontingent (30.9.2026) ───────────────────────────────────

  getAiQuota: async (tenantId: string): Promise<OperatorAiQuota> => {
    const res = await api.get<OperatorAiQuota>(`/admin/tenants/${tenantId}/ai-quota`);
    return res.data;
  },

  setAiQuota: async (tenantId: string, monthlyLimit: number): Promise<OperatorAiQuota> => {
    const res = await api.put<OperatorAiQuota>(`/admin/tenants/${tenantId}/ai-quota`, { monthlyLimit });
    return res.data;
  },

  // ── KI-Vorgaben: Modell je Aufgabe (AP-43) ────────────────────────────────

  getAiTaskDefaults: async (): Promise<AiTaskDefaults> => {
    const res = await api.get<AiTaskDefaults>('/admin/ai-task-defaults');
    return res.data;
  },

  /** `model: null` löscht die Vorgabe. `stand` ist das `updatedAt` der Zeile (AP-36). */
  setAiTaskDefault: async (provider: AiTaskDefaultsProviderKey, task: string, model: string | null, stand?: string): Promise<AiTaskDefaultRow> => {
    const res = await api.put<AiTaskDefaultRow>(`/admin/ai-task-defaults/${provider}/${encodeURIComponent(task)}`, { model }, mitStand(stand));
    return res.data;
  },

  listTenants: async (params: ListTenantsParams): Promise<PaginatedTenants> => {
    const res = await api.get<PaginatedTenants>('/admin/tenants', { params });
    return res.data;
  },

  /** AP-38-I: Die angemeldete Person wird mit ihrem bestehenden Konto Administrator des neuen Mandanten. */
  createTenant: async (payload: { tenantName: string; subdomain: string }): Promise<{ id: string; name: string; subdomain: string }> => {
    const res = await api.post<{ data: { id: string; name: string; subdomain: string } }>('/admin/tenants', payload);
    return res.data.data;
  },

  getTenantModules: async (tenantId: string): Promise<OperatorTenantModules> => {
    const res = await api.get<OperatorTenantModules>(`/admin/tenants/${tenantId}/modules`);
    return res.data;
  },

  bookModule: (tenantId: string, payload: BookModulePayload) =>
    api.post(`/admin/tenants/${tenantId}/modules`, payload),

  updateBooking: (tenantId: string, moduleKey: string, payload: UpdateBookingPayload) =>
    api.patch(`/admin/tenants/${tenantId}/modules/${moduleKey}`, payload),

  /**
   * Umbuchen auf eine Zielmenge — mit `dryRun: true` nur rechnen.
   *
   * Der Weg für alles, was mehr als einen Baustein betrifft: Einzelklicks
   * ließen den Mandanten auf halbem Weg stehen, wenn einer vergessen wird.
   */
  setModules: async (tenantId: string, payload: SetModulesPayload): Promise<SetModulesResult> => {
    const res = await api.put<SetModulesResult>(`/admin/tenants/${tenantId}/modules`, payload);
    return res.data;
  },

  getSeats: async (tenantId: string, moduleKey: string): Promise<SeatOverview> => {
    const res = await api.get<SeatOverview>(`/admin/tenants/${tenantId}/seats/${moduleKey}`);
    return res.data;
  },

  // ── Mandantenpreise (AP-29-N2) ────────────────────────────────────────────

  listTenantPrices: async (tenantId: string): Promise<TenantPriceRow[]> => {
    const res = await api.get<TenantPriceRow[]>(`/admin/tenants/${tenantId}/prices`);
    return res.data;
  },

  setTenantPrice: async (
    tenantId: string,
    itemKey: string,
    payload: {
      amount: number;
      currency?: string;
      basis?: CatalogRow['basis'];
      period?: CatalogRow['period'];
      note?: string;
      /** Ab wann die Zusage gilt; ohne Angabe ab heute. */
      validFrom?: string;
    },
  ): Promise<TenantPriceRow> => {
    const res = await api.put<TenantPriceRow>(
      `/admin/tenants/${tenantId}/prices/${itemKey}`,
      payload,
    );
    return res.data;
  },

  /** Ohne `validFrom` fallen **alle** Zusagen zu diesem Posten weg. */
  removeTenantPrice: (tenantId: string, itemKey: string, validFrom?: string) =>
    api.delete<{ message: string }>(`/admin/tenants/${tenantId}/prices/${itemKey}`, {
      params: validFrom ? { validFrom } : undefined,
    }),

  // ── Paket-Katalog ─────────────────────────────────────────────────────────

  listPackages: async (includeInactive = false): Promise<ModulePackage[]> => {
    const res = await api.get<ModulePackage[]>('/admin/module-packages', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return res.data;
  },

  createPackage: (payload: SaveModulePackagePayload & { key: string }) =>
    api.post<ModulePackage>('/admin/module-packages', payload),

  updatePackage: (key: string, payload: Partial<SaveModulePackagePayload>) =>
    api.patch<ModulePackage>(`/admin/module-packages/${key}`, payload),

  deletePackage: (key: string) => api.delete<{ message: string }>(`/admin/module-packages/${key}`),

  // ── Katalog: Bezeichnung und Preis (AP-29-N) ───────────────────────

  listCatalog: async (): Promise<CatalogRow[]> => {
    const res = await api.get<CatalogRow[]>('/admin/catalog');
    return res.data;
  },

  setCatalogEntry: (
    itemKey: string,
    payload: {
      salesName?: string;
      amount?: number;
      currency?: string;
      basis?: CatalogRow['basis'];
      period?: CatalogRow['period'];
    },
  ) => api.put<CatalogRow>(`/admin/catalog/${itemKey}`, payload),

  removeCatalogEntry: (itemKey: string) =>
    api.delete<{ message: string }>(`/admin/catalog/${itemKey}`),

  bookPackage: async (
    tenantId: string,
    key: string,
    payload: BookPackagePayload,
  ): Promise<BookPackageResult> => {
    const res = await api.post<BookPackageResult>(
      `/admin/tenants/${tenantId}/packages/${key}`,
      payload,
    );
    return res.data;
  },
};
